import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Play, Lock, Clock, ChevronLeft, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import HlsPlayer from '../components/HlsPlayer';
import axios from 'axios';
import { useAuth, API, BACKEND_URL } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import PaymentProviderDialog from '../components/PaymentProviderDialog';

const VideoPage = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const lastProgressPostRef = useRef({ at: 0, seconds: 0 });
  
  const [video, setVideo] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [streamUrl, setStreamUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [payDialogOpen, setPayDialogOpen] = useState(false);

  const buildProgressPayload = useCallback((completed = false) => {
    if (!videoRef.current || !video?.id) return null;
    const currentSeconds = Math.max(0, Math.floor(videoRef.current.currentTime || 0));
    const durationSeconds = Math.max(
      0,
      Math.floor(videoRef.current.duration || video.duration_seconds || 0)
    );
    return {
      video_id: video.id,
      progress_seconds: currentSeconds,
      duration_seconds: durationSeconds,
      completed: completed || (durationSeconds > 0 && currentSeconds >= durationSeconds * 0.9)
    };
  }, [video?.id, video?.duration_seconds]);

  const postWatchProgress = useCallback(async (completed = false, force = false) => {
    if (!user || !token || !video?.id) return;
    const payload = buildProgressPayload(completed);
    if (!payload) return;

    const now = Date.now();
    const last = lastProgressPostRef.current;
    const secondsChanged = Math.abs(payload.progress_seconds - last.seconds);
    if (!force && now - last.at < 15000 && secondsChanged < 10) {
      return;
    }

    lastProgressPostRef.current = { at: now, seconds: payload.progress_seconds };
    setProgress(payload.progress_seconds);

    try {
      await axios.post(`${API}/watch-progress`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) {
      // Progress sync should never interrupt playback.
      console.warn('Failed to sync watch progress', e);
    }
  }, [buildProgressPayload, token, user, video?.id]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!user || !token || !video?.id) return;
      const payload = buildProgressPayload(false);
      if (!payload) return;
      try {
        fetch(`${API}/watch-progress`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload),
          keepalive: true
        });
      } catch (e) {
        // Best-effort only.
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [buildProgressPayload, token, user, video?.id]);

  useEffect(() => {
    const fetchVideo = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${API}/videos/${slug}?locale=${i18n.language}`, { headers });
        setVideo(res.data);
        
        // Fetch category
        const catRes = await axios.get(`${API}/categories?locale=${i18n.language}`);
        const cat = catRes.data.find(c => c.id === res.data.category_id);
        setCategory(cat);
        
        // Fetch watch progress if logged in
        if (token) {
          try {
            const progressRes = await axios.get(`${API}/watch-progress/${res.data.id}`, { headers });
            setProgress(progressRes.data.progress_seconds || 0);
          } catch (e) {
            // No progress saved yet
          }
        }
      } catch (e) {
        console.error('Failed to fetch video', e);
      } finally {
        setLoading(false);
      }
    };
    fetchVideo();
  }, [slug, i18n.language, token]);

  // HLS handled by HlsPlayer component when `isPlaying` and `streamUrl` are set

  const handleRefreshSource = useCallback(async () => {
    if (!user || !token || !video?.id || !video.has_access) {
      throw new Error('User not authorized for stream refresh');
    }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/videos/${video.id}/stream`, { headers });
      return res.data.stream_url;
    } catch (e) {
      console.error('Failed to refresh stream URL', e);
      throw e;
    }
  }, [user, token, video?.id, video?.has_access]);

  const handlePlay = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    if (!video.has_access) {
      toast.error(video.is_free_preview ? 'Free preview is not available for this video' : t('video.locked'));
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/videos/${video.id}/stream`, { headers });
      
      setStreamUrl(res.data.stream_url);
      setIsPlaying(true);
      toast.success(i18n.language === 'el' ? 'Αναπαραγωγή...' : 'Playing...');
    } catch (e) {
      toast.error(e.response?.data?.detail || t('common.error'));
    }
  };

  const handlePurchasePPV = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    setPayDialogOpen(true);
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-misel-bg">
        <Navbar />
        <div className="pt-24 px-8 md:px-16">
          <div className="animate-pulse">
            <div className="aspect-video bg-misel-card rounded-sm mb-8" />
            <div className="h-8 bg-misel-card rounded w-1/3 mb-4" />
            <div className="h-4 bg-misel-card rounded w-2/3" />
          </div>
        </div>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="min-h-screen bg-misel-bg">
        <Navbar />
        <div className="pt-24 px-8 md:px-16 text-center">
          <h1 className="font-roboto font-bold text-2xl text-white">Video not found</h1>
        </div>
      </div>
    );
  }

  const canWatch = video.has_access;
  const previewUnavailable = video.is_free_preview && !video.has_access;
  const ppvPrice = video.is_ppv_enabled && Number(video.ppv_price) > 0 ? Number(video.ppv_price) : 9.99;
  const ppvCurrency = (video.ppv_currency || 'EUR').toUpperCase();
  const ppvAmountLabel = `${ppvCurrency === 'EUR' ? '\u20ac' : `${ppvCurrency} `}${ppvPrice.toFixed(2)}`;

  return (
    <div className="min-h-screen bg-misel-bg">
      <Navbar />
      
      <div className="pt-20">
        {/* Video Player Area */}
        <div className="aspect-video bg-black relative max-w-6xl mx-auto">
          {isPlaying ? (
            <>
              <HlsPlayer
                src={streamUrl}
                backendUrl={BACKEND_URL}
                videoRef={videoRef}
                className="absolute inset-0 w-full h-full bg-black"
                controls
                autoPlay
                playsInline
                onTimeUpdate={() => postWatchProgress(false, false)}
                onPause={() => postWatchProgress(false, true)}
                onEnded={() => postWatchProgress(true, true)}
                onError={(err) => { console.error('HLS player error', err); toast.error('Video playback failed'); }}
                onRefreshSource={handleRefreshSource}
              />
            </>
          ) : (
            <>
              <div 
                className="absolute inset-0 bg-cover bg-center"
                style={{ 
                  backgroundImage: video.thumbnail_url 
                    ? `url('${video.thumbnail_url}')` 
                    : `url('https://images.pexels.com/photos/15659458/pexels-photo-15659458.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`
                }}
              />
              <div className="absolute inset-0 bg-black/60" />
              
              {/* Play/Lock Overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                {canWatch ? (
                  <button
                    onClick={handlePlay}
                    data-testid="play-video-btn"
                    className="w-20 h-20 rounded-full bg-primary/90 hover:bg-primary flex items-center justify-center transition-all hover:scale-110"
                  >
                    <Play size={36} className="text-white ml-1" fill="white" />
                  </button>
                ) : (
                  <div className="text-center">
                    <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center mb-4 mx-auto">
                      <Lock size={36} className="text-white/70" />
                    </div>
                    <p className="text-white font-medium mb-4">
                      {previewUnavailable ? 'Free preview is not available for this video' : t('video.locked')}
                    </p>
                    <Button
                      onClick={handlePurchasePPV}
                      disabled={purchaseLoading}
                      data-testid="buy-ppv-btn"
                      className="bg-primary hover:bg-primary/90"
                    >
                      <ShoppingCart size={18} className="mr-2" />
                      {purchaseLoading ? t('common.loading') : `${t('video.buy_access')} - ${ppvAmountLabel}`}
                    </Button>
                  </div>
                )}
              </div>
              
              {/* Progress bar */}
              {progress > 0 && canWatch && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
                  <div 
                    className="h-full bg-primary"
                    style={{ width: `${(progress / video.duration_seconds) * 100}%` }}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Video Info */}
        <div className="max-w-6xl mx-auto px-8 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {category && (
              <Link 
                to={`/category/${category.slug}`}
                className="inline-flex items-center gap-2 text-primary hover:text-primary/80 mb-4 text-sm"
              >
                <ChevronLeft size={16} />
                {category.title}
              </Link>
            )}
            
            <h1 className="font-roboto font-bold text-2xl md:text-3xl text-white tracking-wide mb-4">
              {video.title}
            </h1>
            
            <div className="flex items-center gap-4 text-gray-400 text-sm mb-6">
              <span className="flex items-center gap-1">
                <Clock size={16} />
                {formatDuration(video.duration_seconds)}
              </span>
              {video.is_free_preview && (
                <span className="px-2 py-1 bg-primary/20 text-primary rounded-sm text-xs">
                  {previewUnavailable ? 'Preview unavailable' : t('video.free_preview')}
                </span>
              )}
              {video.has_access && (
                <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-sm text-xs">
                  {i18n.language === 'el' ? 'Έχεις πρόσβαση' : 'Access granted'}
                </span>
              )}
            </div>
            
            <p className="text-gray-400 leading-relaxed max-w-3xl">
              {video.description}
            </p>

            {/* CTA for non-subscribers */}
            {!video.has_access && (
              <div className="mt-8 p-6 bg-misel-card rounded-sm border border-white/10">
                <h3 className="font-roboto font-bold text-lg text-white mb-2">
                  {i18n.language === 'el' ? 'Αποκτήστε πρόσβαση' : 'Get Access'}
                </h3>
                <p className="text-gray-400 text-sm mb-4">
                  {i18n.language === 'el' 
                    ? 'Αγοράστε αυτό το βίντεο ή εγγραφείτε για απεριόριστη πρόσβαση'
                    : 'Purchase this video or subscribe for unlimited access'
                  }
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button
                    onClick={handlePurchasePPV}
                    disabled={purchaseLoading}
                    data-testid="buy-ppv-btn-2"
                    className="bg-primary hover:bg-primary/90"
                  >
                    <ShoppingCart size={18} className="mr-2" />
                    {purchaseLoading ? t('common.loading') : `${t('video.buy_access')} - ${ppvAmountLabel}`}
                  </Button>
                  <Link to="/pricing">
                    <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                      {t('pricing.subscribe')}
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      <PaymentProviderDialog
        open={payDialogOpen}
        onOpenChange={setPayDialogOpen}
        mode="ppv"
        videoId={video?.id}
        basePrice={ppvPrice}
        amountLabel={ppvAmountLabel}
      />
    </div>
  );
};

export default VideoPage;
